import * as d3 from 'd3'
import { dataHasSubCircles } from '../helpers/nodeKind'
import { Data, DiscLink, LinkLegendEntry, NodeData, NodeType } from '../types'
import { packData } from './layouts/pack'

// #107, "Linked circles" view: circles side by side instead of nested.
//
// Decided with Yohan on 22/09/2026 (notes on #107, then his test on gouv):
// the org at the centre; each sub-circle TOUCHES its super-circle, and the
// higher a circle in the org, the bigger and the darker its disc. A disc holds
// its own roles, its link roles (leader, representative…) grouped at the
// centre. Where a sub-circle touches its super-circle, a clasp: one line per
// link role of the sub-circle (discLinks, drawn over the discs).
//
// Link roles are told apart by their base role, else by their exact name,
// never by an expected name: up to LINK_COLORS of them get a colour each (the
// same in their disc and in the clasps, named in linkLegend); beyond,
// the clasps are grey and there is no legend.
//
// 🔑 Every disc becomes a DIRECT child of the artificial root. The engine's
// culling relies on "a child lies inside its parent" (culling.ts) to skip a
// subtree off screen; keeping the org tree here would hide a sub-circle as
// soon as its super-circle leaves the screen. The super-circle relation only
// drives the placement, the colour, the size and the clasps.
//
// #107, "Network" view (Yohan, 23/09): exactly this view, but a disc's roles
// sit AROUND it instead of inside (option rolesAround). The disc holds
// nothing, not even its circle's own members; its roles, members included,
// form a crown in the room its lines leave free, and are direct children of
// the artificial root too (a role outside its disc would break the culling
// rule above). One light line joins a disc to its super-circle, one to each
// of its roles, drawn under the nodes: no clasp, no legend.

type Sort = (a: d3.HierarchyNode<Data>, b: d3.HierarchyNode<Data>) => number

interface Disc {
  data: Data
  superId: string | null
  level: number
  // Own nodes' offsets from the disc centre, in packed units (before scale)
  offsets: Map<string, { x: number; y: number; r: number }>
  linkRoles: Data[]
  // rolesAround: its roles, each packed on its own, in crown order
  crown: NodeData[]
  // rolesAround: how far the crown reaches beyond the disc
  crownDepth: number
  packedR: number
  scale: number
  r: number
  x: number
  y: number
}

export interface LinkedCirclesOptions {
  // Roles around their disc instead of inside it (Network view)
  rolesAround?: boolean
}

// All sizes below are relative to the org's disc radius.
// Space between two discs that are not linked: they never touch
const GAP_RATIO = 0.25
// Space between a sub-circle and its super-circle: close, but apart (Yohan,
// 22/09: the clasp shows in the gap)
const CONTACT_RATIO = 0.18
// Clasp: length across the contact point, distance between its lines
const CLASP_LENGTH_RATIO = 0.3
const CLASP_SPACING_RATIO = 0.07
// Reading direction: up to FAN_MAX sub-circles fan out on the right, one
// FAN_STEPS[n] apart (2: up and down at 45°; 3: 60° apart; 4: 45° apart)
const FAN_MAX = 4
const FAN_STEPS = [0, 0, Math.PI / 2, Math.PI / 3, Math.PI / 4]
// A disc's size depends on its level only: the org is the biggest, each level
// below smaller, never below this share of the org. Its roles are scaled to
// fit, however many they are
const levelSize = (level: number) => Math.max(0.4, 1 - 0.2 * level)
// rolesAround: every role gets the same radius, whatever its members (they
// shrink to fit inside): in packed units, about a role with four members.
// The org's disc is this many times as wide, the discs below it by level
const ROLE_R = 25
const ORG_TO_ROLE = 8
// rolesAround: space between a disc and its crown, and between two roles, as
// a share of the disc's biggest role
const CROWN_PAD_RATIO = 0.3
// rolesAround: from this many roles, a disc is busy: its crown keeps even its
// super-circle and sub-circles at a distance
const BUSY_ROLES = 8
// ... by this share of one band of its crown: the crown spreads along the
// disc rather than outwards, one band is what stands in the way
const BUSY_SHARE = 0.7
// rolesAround: the space between two discs, this many times as wide
const SPREAD = 1.5
// rolesAround: share of a crown's depth that keeps unlinked discs apart
const CROWN_REACH = 0.6
// Colours of the link roles (hues), in legend order. Kept in sync with
// DiscLinks, which draws the clasps and the legend
export const LINK_ROLE_HUES = [35, 190, 285, 135]
// Link roles when they are too many to colour: amber in their disc
const LINK_ROLE_DEFAULT_HUE = 35

// A circle drawn as a disc: it has at least one sub-circle or role
const isDisc = dataHasSubCircles

// What the placement produces. core/layouts/linked.ts turns it into the
// engine's Layout (node geometry, bounds, focus).
export interface LinkedCircles {
  root: NodeData
  nodes: NodeData[]
  links: DiscLink[]
  linkLegend: LinkLegendEntry[]
}

export function layoutLinkedCircles(
  data: Data,
  sort: Sort,
  { rolesAround = false }: LinkedCirclesOptions = {}
): LinkedCircles {
  // 0. The org's link roles and their colours
  const linkLegend = legendOf(data)
  const colorIndexOf = new Map(
    linkLegend.map((entry) => [entry.key, entry.colorIndex])
  )
  const hueOf = (role: Data) => {
    const index = role.linkKey ? colorIndexOf.get(role.linkKey) : undefined
    return index === undefined ? LINK_ROLE_DEFAULT_HUE : LINK_ROLE_HUES[index]
  }
  // Legend order: a link role keeps its place in every clasp
  const legendRank = (role: Data) => {
    const index = linkLegend.findIndex((entry) => entry.key === role.linkKey)
    return index === -1 ? Infinity : index
  }

  // 1. One disc per circle, with only what it holds itself
  const discs: Disc[] = []
  const collect = (circle: Data, superId: string | null, level: number) => {
    const children = circle.children ?? []
    const own = children
      .filter((child) => !isDisc(child))
      .map((child) =>
        child.parentLink ? { ...child, colorHue: hueOf(child) } : child
      )
    const linkRoles = own
      .filter((child) => child.parentLink)
      .sort(
        (a, b) => legendRank(a) - legendRank(b) || a.name.localeCompare(b.name)
      )
    const plainRoles = own.filter((child) => !child.parentLink)
    // Around: roles (link roles first) out of the disc
    const crownRoles = rolesAround
      ? [...linkRoles, ...plainRoles].filter(
          (child) => child.type === NodeType.Circle
        )
      : []
    // Around: nothing inside, not even the circle's own members (Yohan, 23/09)
    const inside = rolesAround ? [] : own
    const local: Data = {
      ...circle,
      children: inside.length
        ? inside
        : [
            {
              id: `${circle.id}-empty`,
              parentId: circle.id,
              name: '',
              type: NodeType.MembersCircle,
            },
          ],
    }
    const { offsets, r } = rolesAround
      ? packDisc(local, [], [], sort)
      : packDisc(local, linkRoles, plainRoles, sort)
    discs.push({
      data: local,
      superId,
      level,
      offsets,
      linkRoles,
      crown: crownRoles.map((role) => packData(role, sort) as unknown as NodeData),
      crownDepth: 0,
      // Set once every disc is packed (sizes by level)
      packedR: r,
      scale: 1,
      r,
      x: 0,
      y: 0,
    })
    for (const child of children) {
      if (isDisc(child)) collect(child, circle.id, level + 1)
    }
  }
  for (const top of data.children ?? []) collect(top, null, 0)

  if (discs.length === 0) {
    const root = d3.hierarchy(data) as NodeData
    Object.assign(root, { x: 0, y: 0, r: 1 })
    return { root, nodes: [], links: [], linkLegend }
  }

  // Sizes by level, the org's disc being as big as the biggest packing.
  // Around: roles are not in the discs, the org's disc is sized on a
  // role (ORG_TO_ROLE), and the discs' own members keep the roles' scale
  const orgR = rolesAround
    ? Math.max(
        ROLE_R * ORG_TO_ROLE,
        ...discs.map((disc) => disc.packedR / levelSize(disc.level))
      )
    : Math.max(...discs.map((disc) => disc.packedR))
  for (const disc of discs) {
    disc.r = orgR * levelSize(disc.level)
    disc.scale = rolesAround
      ? Math.min(1, disc.r / disc.packedR)
      : disc.r / disc.packedR
    disc.crownDepth = crownDepthOf(disc)
  }
  const gap = orgR * GAP_RATIO
  const contact = orgR * CONTACT_RATIO
  // Around: a crown takes room from its unlinked neighbours. It seldom goes
  // all the way round, and a role in the way slides along it (unpileSeats):
  // only part of its depth keeps them apart
  const reach = (disc: Disc) => disc.r + disc.crownDepth * CROWN_REACH
  // A sub-circle against its super-circle. Around, a busy disc keeps its
  // neighbours further off, its crown all the way between them (Yohan,
  // 23/09: SCIC and Accompagnements, Accompagnements and Offres inter-orga)
  const busyDepth = (disc: Disc) =>
    rolesAround && disc.crown.length >= BUSY_ROLES
      ? Math.min(disc.crownDepth, bandDepth(disc) * BUSY_SHARE)
      : 0
  // Around, the space between two discs' edges is SPREAD times as wide
  // (Yohan, 23/09: easier to read)
  const spread = rolesAround ? SPREAD : 1
  const linkedDistance = (a: Disc, b: Disc) =>
    a.r + b.r + (contact + busyDepth(a) + busyDepth(b)) * spread
  const apartDistance = (a: Disc, b: Disc) =>
    a.r + b.r + (reach(a) - a.r + reach(b) - b.r + gap) * spread

  // 2. First placement, with a reading direction (Yohan, 22/09): up to
  // FAN_MAX sub-circles fan out on the "right" of their super-circle — one
  // straight to the right, two up and down on the right, and so on, in their
  // order. The org's right is the screen's right; a sub-circle's right points
  // away from its own super-circle, so its sub-circles never fold back.
  // Beyond FAN_MAX: petals, a heavy branch getting a wider share of the ring,
  // the ring turned so that the heaviest branch still points to the right
  const childrenOf = (id: string | null) =>
    discs.filter((disc) => disc.superId === id)
  const weight = (disc: Disc): number =>
    disc.r + childrenOf(disc.data.id).reduce((sum, sub) => sum + weight(sub), 0)
  const putAgainst = (disc: Disc, sub: Disc, angle: number) => {
    const distance = linkedDistance(disc, sub)
    sub.x = disc.x + Math.cos(angle) * distance
    sub.y = disc.y + Math.sin(angle) * distance
  }

  const place = (disc: Disc, direction: number, petalSpread: number) => {
    const subs = childrenOf(disc.data.id)
    if (subs.length === 0) return
    if (subs.length <= FAN_MAX) {
      const step = FAN_STEPS[subs.length]
      subs.forEach((sub, index) => {
        const angle = direction + (index - (subs.length - 1) / 2) * step
        putAgainst(disc, sub, angle)
        place(sub, angle, Math.PI)
      })
      return
    }
    const weights = subs.map(weight)
    const total = weights.reduce((sum, w) => sum + w, 0)
    const shares = weights.map((w) => (petalSpread * w) / total)
    const centres = shares.map(
      (share, index) =>
        shares.slice(0, index).reduce((sum, s) => sum + s, 0) + share / 2
    )
    const heaviest = weights.indexOf(Math.max(...weights))
    subs.forEach((sub, index) => {
      const share = shares[index]
      const angle = direction + centres[index] - centres[heaviest]
      putAgainst(disc, sub, angle)
      // Grand-children fan out away from the centre, within their share
      place(sub, angle, Math.min(Math.PI, share + Math.PI / 2))
    })
  }
  const tops = childrenOf(null)
  tops.forEach((top, index) => {
    top.x = index * (top.r * 2 + gap) * 3
    top.y = 0
    place(top, 0, 2 * Math.PI)
  })

  // 3. Settle: each sub-circle against its super-circle, every other pair of
  // discs apart by at least the gap. The org stays put
  const isLinked = (a: Disc, b: Disc) =>
    a.superId === b.data.id || b.superId === a.data.id
  const org = discs[0]
  for (let iteration = 0; iteration < 400; iteration++) {
    for (let i = 0; i < discs.length; i++) {
      for (let j = i + 1; j < discs.length; j++) {
        const a = discs[i]
        const b = discs[j]
        const dx = b.x - a.x
        const dy = b.y - a.y
        const distance = Math.hypot(dx, dy) || 1
        const linked = isLinked(a, b)
        const wanted = linked ? linkedDistance(a, b) : apartDistance(a, b)
        // Linked discs are held at contact, the others only pushed apart
        if (!linked && distance >= wanted) continue
        const move = (wanted - distance) / distance
        // Unlinked pushes are full, linked pulls gentle (they settle over
        // the iterations without breaking the separation)
        const strength = linked ? 0.3 : 0.5
        const [shareA, shareB] =
          a === org ? [0, 1] : b === org ? [1, 0] : [0.5, 0.5]
        a.x -= dx * move * strength * shareA
        a.y -= dy * move * strength * shareA
        b.x += dx * move * strength * shareB
        b.y += dy * move * strength * shareB
      }
    }
  }
  // Last passes: separation only, so that no disc overlaps another. Around,
  // the crowns push harder: a sub-circle is first held back against its
  // super-circle, the clasp in the gap between them
  const separate = (holdLinked: boolean) => {
    for (let iteration = 0; iteration < 50; iteration++) {
      for (let i = 0; i < discs.length; i++) {
        for (let j = i + 1; j < discs.length; j++) {
          const a = discs[i]
          const b = discs[j]
          const dx = b.x - a.x
          const dy = b.y - a.y
          const distance = Math.hypot(dx, dy) || 1
          const wanted = isLinked(a, b)
            ? linkedDistance(a, b)
            : apartDistance(a, b)
          if (distance >= wanted && !(holdLinked && isLinked(a, b))) continue
          const move = (wanted - distance) / distance
          const [shareA, shareB] =
            a === org ? [0, 1] : b === org ? [1, 0] : [0.5, 0.5]
          a.x -= dx * move * shareA
          a.y -= dy * move * shareA
          b.x += dx * move * shareB
          b.y += dy * move * shareB
        }
      }
    }
  }
  if (rolesAround) separate(true)
  separate(false)

  // 4. Colour: the top of the org is darkest. getColor darkens with depth
  // (light mode), so the top gets the HIGHEST colour depth, then lighter
  const maxLevel = Math.max(...discs.map((disc) => disc.level))
  const paint = (node: Data, depth: number) => {
    node.colorDepth = depth
    node.children?.forEach((child) => paint(child, depth + 1))
  }
  for (const disc of discs) {
    paint(disc.data, maxLevel - disc.level + 1)
    for (const role of disc.crown) paint(role.data, maxLevel - disc.level + 2)
  }

  // 5. Build the flat tree (root → discs → their roles) with final positions,
  // the org at (0, 0) — where the engine centres its pan extent — so fitting
  // the root on screen puts the org in the middle and every disc is reachable
  const cx = org.x
  const cy = org.y
  const positions = new Map<string, { x: number; y: number; r: number }>()
  for (const disc of discs) {
    positions.set(disc.data.id, { x: disc.x - cx, y: disc.y - cy, r: disc.r })
    for (const [id, offset] of disc.offsets) {
      positions.set(id, {
        x: disc.x - cx + offset.x * disc.scale,
        y: disc.y - cy + offset.y * disc.scale,
        r: offset.r * disc.scale,
      })
    }
  }
  // Around: each role on its crown, then the roles that land on a disc or on
  // another role slide along their ring until they are clear
  const seats: Seat[] = discs.flatMap((disc) => {
    const neighbours = discs
      .filter(
        (other) =>
          other.superId === disc.data.id || disc.superId === other.data.id
      )
      .map((other) => Math.atan2(other.y - disc.y, other.x - disc.x))
    return crownOffsets(disc, neighbours).map((offset, index) => ({
      disc,
      role: disc.crown[index],
      x: disc.x + offset.x,
      y: disc.y + offset.y,
      distance: Math.hypot(offset.x, offset.y),
    }))
  })
  unpileSeats(seats, discs)
  for (const { role, x, y } of seats) {
    for (const node of role.descendants()) {
      positions.set(node.data.id, {
        x: x - cx + (node.x - role.x) * (ROLE_R / role.r),
        y: y - cy + (node.y - role.y) * (ROLE_R / role.r),
        r: node.r * (ROLE_R / role.r),
      })
    }
  }
  const rootData: Data = {
    ...data,
    children: [
      ...discs.map((disc) => disc.data),
      ...discs.flatMap((disc) => disc.crown.map((role) => role.data)),
    ],
  }
  const root = d3.hierarchy(rootData) as NodeData
  // Root: a circle enclosing every disc (culling's spatial index)
  const rootR =
    Math.max(
      ...[...positions.values()].map((p) => Math.hypot(p.x, p.y) + p.r)
    ) + gap
  Object.assign(root, { x: 0, y: 0, r: rootR })
  for (const node of root.descendants().slice(1)) {
    Object.assign(node, positions.get(node.data.id) ?? { x: 0, y: 0, r: 0 })
  }

  // Same final scale as the nested layout: the smallest node is 30 wide
  const all = root.descendants()
  const minRadius = all.reduce(
    (min, node) => (node.r > 0 && node.r < min ? node.r : min),
    Infinity
  )
  const nodeScale = Number.isFinite(minRadius) ? 30 / minRadius : 1
  for (const node of all) {
    node.r *= nodeScale
    node.x *= nodeScale
    node.y *= nodeScale
  }

  // 6. Clasps: across each contact point, one line per link role
  const nodeById = new Map(all.map((node) => [node.data.id, node]))
  const links: DiscLink[] = []
  // Around (Yohan, 23/09): no clasp, a single grey line from a disc to its
  // super-circle, and a thinner one from a disc to each of its roles, edge
  // to edge
  if (rolesAround) {
    const line = (
      id: string,
      from: NodeData,
      to: NodeData,
      width: number
    ): DiscLink => {
      const length = Math.hypot(to.x - from.x, to.y - from.y) || 1
      const [ux, uy] = [(to.x - from.x) / length, (to.y - from.y) / length]
      return {
        id,
        x1: from.x + ux * from.r,
        y1: from.y + uy * from.r,
        x2: to.x - ux * to.r,
        y2: to.y - uy * to.r,
        spacing: 0,
        width,
        lines: [{ key: id, colorIndex: null }],
        tone: 'light',
      }
    }
    const unit = ROLE_R * nodeScale
    for (const disc of discs) {
      const node = nodeById.get(disc.data.id)
      if (!node) continue
      const sup = disc.superId ? nodeById.get(disc.superId) : undefined
      if (sup) links.push(line(disc.data.id, node, sup, unit * 0.16))
      for (const role of disc.crown) {
        const roleNode = nodeById.get(role.data.id)
        if (roleNode) {
          links.push(line(role.data.id, node, roleNode, unit * 0.06))
        }
      }
    }
    return { root, nodes: all.slice(1), links, linkLegend: [] }
  }
  const half = (orgR * nodeScale * CLASP_LENGTH_RATIO) / 2
  for (const disc of discs) {
    const sub = nodeById.get(disc.data.id)
    const sup = disc.superId ? nodeById.get(disc.superId) : undefined
    if (!sub || !sup || disc.linkRoles.length === 0) continue
    const length = Math.hypot(sup.x - sub.x, sup.y - sub.y) || 1
    const [ux, uy] = [(sup.x - sub.x) / length, (sup.y - sub.y) / length]
    // Contact point: midway between the two edges
    const edges = (length - sub.r - sup.r) / 2
    const mx = sub.x + ux * (sub.r + edges)
    const my = sub.y + uy * (sub.r + edges)
    links.push({
      id: disc.data.id,
      x1: mx - ux * half,
      y1: my - uy * half,
      x2: mx + ux * half,
      y2: my + uy * half,
      spacing: orgR * nodeScale * CLASP_SPACING_RATIO,
      lines: disc.linkRoles.map((role) => ({
        key: role.linkKey ?? role.id,
        colorIndex: role.linkKey
          ? colorIndexOf.get(role.linkKey) ?? null
          : null,
      })),
    })
  }

  return { root, nodes: all.slice(1), links, linkLegend }
}

// The org's link roles, one entry per identity (base role, else exact name):
// base roles first, then by name. Too many to tell apart by colour: none
function legendOf(data: Data): LinkLegendEntry[] {
  const byKey = new Map<string, string>()
  const walk = (node: Data) => {
    if (node.parentLink && node.linkKey && !byKey.has(node.linkKey)) {
      byKey.set(node.linkKey, node.name)
    }
    node.children?.forEach(walk)
  }
  walk(data)
  if (byKey.size > LINK_ROLE_HUES.length) return []
  return [...byKey.entries()]
    .sort(
      ([keyA, nameA], [keyB, nameB]) =>
        Number(keyB.startsWith('role:')) - Number(keyA.startsWith('role:')) ||
        nameA.localeCompare(nameB)
    )
    .map(([key, name], colorIndex) => ({ key, name, colorIndex }))
}

// Packs a disc's own roles (as the nested view does), then moves its link
// roles to the centre: they are packed as one group, and every offset is taken
// from that group, so the disc is centred on it. The disc's radius grows to
// still hold every role, keeping the packing's padding.
function packDisc(
  local: Data,
  linkRoles: Data[],
  plainRoles: Data[],
  sort: Sort
): { offsets: Map<string, { x: number; y: number; r: number }>; r: number } {
  const groupId = `${local.id}-links`
  const input: Data =
    linkRoles.length === 0
      ? local
      : {
          ...local,
          children: [
            {
              id: groupId,
              parentId: local.id,
              name: '',
              type: NodeType.MembersCircle,
              children: linkRoles,
            },
            ...plainRoles,
          ],
        }
  // The group goes first: d3.pack starts from the first sibling, so it lands
  // near the middle and centring the disc on it barely grows the disc
  const groupFirst: Sort = (a, b) =>
    a.data.id === groupId ? -1 : b.data.id === groupId ? 1 : sort(a, b)
  const packed = packData(input, groupFirst) as unknown as NodeData
  const group = packed.children?.find((node) => node.data.id === groupId)
  const centre = group ?? packed

  const offsets = new Map<string, { x: number; y: number; r: number }>()
  for (const node of packed.descendants()) {
    if (node === packed || node === group) continue
    offsets.set(node.data.id, {
      x: node.x - centre.x,
      y: node.y - centre.y,
      r: node.r,
    })
  }
  if (!group) return { offsets, r: packed.r }

  // Padding the packing left around its children, kept around the new centre
  const reach = (node: NodeData, from: NodeData) =>
    Math.hypot(node.x - from.x, node.y - from.y) + node.r
  const children = packed.children ?? []
  const padding = packed.r - Math.max(...children.map((c) => reach(c, packed)))
  const r = Math.max(...children.map((c) => reach(c, group))) + padding
  return { offsets, r }
}

// rolesAround: the crown's bands. A band holds two rows, the roles one in one
// out, so that the line to an outer role passes between two inner ones
// (Yohan, 23/09: roles on two levels). A band full, the next one further out.
// Between the disc and its first row, room for the lines to show
const crownPad = (disc: Disc) => crownMaxR(disc) * CROWN_PAD_RATIO
const crownMaxR = (disc: Disc) => (disc.crown.length ? ROLE_R : 0)
const LINE_ROOM = ROLE_R * 0.5
// The outer row, this much further out than the inner one
const ROW_STEP = ROLE_R * 1.4
// Up to this many roles, a single row
const SINGLE_ROW_MAX = 3
const bandDepth = (disc: Disc) =>
  ROW_STEP + 2 * crownMaxR(disc) + crownPad(disc) + LINE_ROOM
const bandDistance = (disc: Disc, band: number) =>
  disc.r + LINE_ROOM + crownMaxR(disc) + band * bandDepth(disc)
const twoRows = (disc: Disc) => disc.crown.length > SINGLE_ROW_MAX
// Angle between two neighbours of the crown: on one row, side by side; on
// two, an inner and an outer one clear of each other
const crownStep = (disc: Disc, band: number) =>
  (twoRows(disc) ? ROLE_R * 1.85 : 2 * crownMaxR(disc) + crownPad(disc)) /
  bandDistance(disc, band)

// Depth of the crown before the discs are placed: its lines leave about a
// quarter of each band taken
function crownDepthOf(disc: Disc): number {
  if (disc.crown.length === 0) return 0
  let left = disc.crown.length
  let band = 0
  while (left > 0) {
    left -= Math.max(1, Math.floor((Math.PI * 1.5) / crownStep(disc, band)))
    band++
  }
  return band * bandDepth(disc) - LINE_ROOM + crownPad(disc)
}

// Where each role of the crown sits, from the disc's centre: in the arcs its
// lines (to its super-circle and sub-circles) leave free, the widest first,
// each group centred in its arc; a ring full, the next one further out
function crownOffsets(
  disc: Disc,
  neighbours: number[]
): Array<{ x: number; y: number }> {
  const offsets: Array<{ x: number; y: number }> = []
  const sorted = [...neighbours].sort((a, b) => a - b)
  const rows = twoRows(disc)
  let band = 0
  while (offsets.length < disc.crown.length) {
    const distance = bandDistance(disc, band)
    const step = crownStep(disc, band)
    const arcs: Array<{ start: number; length: number }> = []
    if (sorted.length === 0) {
      // Nothing around: the crown hangs from the top
      arcs.push({ start: -Math.PI / 2 - Math.PI, length: 2 * Math.PI })
    } else {
      // Clear of a line by a role
      const margin =
        Math.asin(Math.min(1, (crownMaxR(disc) + crownPad(disc)) / distance)) +
        0.1
      sorted.forEach((angle, index) => {
        const next =
          index + 1 < sorted.length
            ? sorted[index + 1]
            : sorted[0] + 2 * Math.PI
        const length = next - angle - 2 * margin
        if (length >= 0) arcs.push({ start: angle + margin, length })
      })
    }
    arcs.sort((a, b) => b.length - a.length)
    for (const arc of arcs) {
      const left = disc.crown.length - offsets.length
      if (left === 0) break
      const closed = arc.length >= 2 * Math.PI
      let capacity = closed
        ? Math.max(1, Math.floor(arc.length / step))
        : Math.floor(arc.length / step) + 1
      // All the way round on two rows: an even count, so that the first and
      // the last are not both inner
      if (closed && rows && capacity > 1) capacity -= capacity % 2
      const count = Math.min(left, capacity)
      const gap = closed ? arc.length / count : step
      const centre = arc.start + arc.length / 2
      for (let i = 0; i < count; i++) {
        const angle = centre + (i - (count - 1) / 2) * gap
        const reach = distance + (rows && i % 2 === 1 ? ROW_STEP : 0)
        offsets.push({ x: Math.cos(angle) * reach, y: Math.sin(angle) * reach })
      }
    }
    band++
    // Safety: never loop forever on a crowded disc
    if (band > 50) break
  }
  return offsets
}

interface Seat {
  disc: Disc
  role: NodeData
  x: number
  y: number
  // From the disc's centre: a role keeps it while it slides
  distance: number
}

// Roles that overlap a disc or another role are pushed apart, then brought
// back to their own ring: they slide along it, their disc never moves
function unpileSeats(seats: Seat[], discs: Disc[]) {
  for (let iteration = 0; iteration < 150; iteration++) {
    let moved = false
    for (let i = 0; i < seats.length; i++) {
      const a = seats[i]
      const pad = crownPad(a.disc)
      for (let j = i + 1; j < seats.length; j++) {
        const b = seats[j]
        const dx = b.x - a.x
        const dy = b.y - a.y
        const distance = Math.hypot(dx, dy) || 1
        const wanted = 2 * ROLE_R + pad
        if (distance >= wanted) continue
        const move = (wanted - distance) / distance / 2
        a.x -= dx * move
        a.y -= dy * move
        b.x += dx * move
        b.y += dy * move
        moved = true
      }
      for (const disc of discs) {
        const dx = a.x - disc.x
        const dy = a.y - disc.y
        const distance = Math.hypot(dx, dy) || 1
        const wanted = disc.r + ROLE_R + pad
        if (distance >= wanted) continue
        const move = (wanted - distance) / distance
        a.x += dx * move
        a.y += dy * move
        moved = true
      }
    }
    // Back on the ring, at the same angle
    for (const seat of seats) {
      const angle = Math.atan2(seat.y - seat.disc.y, seat.x - seat.disc.x)
      seat.x = seat.disc.x + Math.cos(angle) * seat.distance
      seat.y = seat.disc.y + Math.sin(angle) * seat.distance
    }
    if (!moved) break
  }
}
